import type { ReactNode } from 'react';
import { AlertCircle, UserRoundX } from '@/lib/icons';
import {
  Button,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
} from 'prosci-ui';

interface ReportLoadingStateProps {
  title?: string;
  description?: string;
}

/**
 * Loading state for report data
 */
export function ReportLoadingState({
  title = 'Loading report',
  description = 'Please wait while we fetch the report data...',
}: ReportLoadingStateProps) {
  return (
    <Empty>
      <EmptyIcon>
        <Spinner size="small" />
      </EmptyIcon>
      <EmptyTitle>{title}</EmptyTitle>
      <EmptyDescription>{description}</EmptyDescription>
    </Empty>
  );
}

interface ReportErrorStateProps {
  error: Error | unknown;
}

/**
 * Error state for report loading failures
 */
export function ReportErrorState({ error }: ReportErrorStateProps) {
  return (
    <Empty>
      <EmptyIcon variant="destructive">
        <AlertCircle />
      </EmptyIcon>
      <EmptyTitle>Error loading report</EmptyTitle>
      <EmptyDescription>
        {error instanceof Error ? error.message : 'An unexpected error occurred'}
      </EmptyDescription>
    </Empty>
  );
}

interface ReportEmptyStateProps {
  icon?: ReactNode;
  title?: string;
  description?: string;
  hasFilters?: boolean;
  onClearFilters?: () => void;
}

/**
 * Empty state when no data is found
 */
export function ReportEmptyState({
  icon,
  title = 'No data found',
  description = 'Try adjusting your filters to find data.',
  hasFilters = false,
  onClearFilters,
}: ReportEmptyStateProps) {
  return (
    <Empty>
      <EmptyIcon>{icon || <UserRoundX />}</EmptyIcon>
      <EmptyTitle>{title}</EmptyTitle>
      <EmptyDescription>{description}</EmptyDescription>
      {hasFilters && onClearFilters && (
        <Button type="button" onClick={onClearFilters} variant="outline">
          Clear Filters
        </Button>
      )}
    </Empty>
  );
}

interface ReportAccessDeniedStateProps {
  message?: string;
}

/**
 * Access denied state for restricted reports
 */
export function ReportAccessDeniedState({
  message = 'You do not have permission to view this report.',
}: ReportAccessDeniedStateProps) {
  return (
    <Empty>
      <EmptyIcon variant="destructive">
        <AlertCircle />
      </EmptyIcon>
      <EmptyTitle>Access Denied</EmptyTitle>
      <EmptyDescription>{message}</EmptyDescription>
    </Empty>
  );
}
